/** /vehicles — cross-tenant Fleet Vehicles page (List | Map tabs).
 *
 * List is the roster grid; Map plots live positions for a selected tenant. The
 * active tab lives in `?view=` (so it survives reload + is deep-linkable, e.g.
 * from an operator's fleet integration). The Map tab is lazy so the Google Maps
 * SDK stays off the critical path for the common (list) case.
 */
import { Box, CircularProgress, Tab, Tabs } from '@mui/material';
import React, { Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';

import { PageHeader } from '@/components/PageHeader';
import { PAGE_FILL_SX } from '@/layout/pageFill';

import { VehiclesListTab } from './VehiclesListTab';

const VehiclesMapTab = React.lazy(() =>
  import('./VehiclesMapTab').then((m) => ({ default: m.VehiclesMapTab })),
);

export function VehiclesPage() {
  const [sp, setSp] = useSearchParams();
  const view = sp.get('view') === 'map' ? 'map' : 'list';
  const setView = (v: 'list' | 'map') =>
    setSp((prev) => {
      const next = new URLSearchParams(prev);
      if (v === 'map') next.set('view', 'map');
      else next.delete('view');
      return next;
    });

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="vehicles-roster"
        title="Vehicles"
        subtitle="Cross-tenant fleet roster — edit vehicles, assign routes, and view live positions on the map."
      />
      <Tabs value={view} onChange={(_, v) => setView(v as 'list' | 'map')} sx={{ mb: 2 }}>
        <Tab value="list" label="List" />
        <Tab value="map" label="Map" />
      </Tabs>
      {view === 'list' ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <VehiclesListTab />
        </Box>
      ) : (
        <Suspense
          fallback={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '62vh',
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
          <VehiclesMapTab />
        </Suspense>
      )}
    </Box>
  );
}
